
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { DateTime } from 'luxon';

import { sharedState } from '../../../../framework/state';
import { dateToDbMonthDateString } from '../../../../../common/framework/util/convert';

import BudgetSpent from './BudgetSpent.vue';
import VehicleTypeTable from './VehicleTypeTable.vue';
import VehicleStateChart from './VehicleStateChart.vue';
import MonthlyBudgetChart from './MonthlyBudgetChart.vue';
import BudgetTable from './BudgetTable.vue';
import CostTypeChart from './CostTypeChart.vue';
import VehicleMakeTable from './VehicleMakeTable.vue';
import VehicleMakeChart from './VehicleMakeChart.vue';
import AutoCompleteMultipleField from '../../../fields/AutoCompleteMultipleField.vue';
import VehicleMonthlyMileageChart from './VehicleMonthlyMileageChart.vue';
import VehicleMonthlyConsumptionChart from './VehicleMonthlyConsumptionChart.vue';
import VehicleMonthlyCO2Chart from './VehicleMonthlyCO2Chart.vue';
import VehicleMonthlyAverageMileageChart from './VehicleMonthlyAverageMileageChart.vue';
import VehicleStates from './VehicleStates.vue';
import MonthlyVehicleContractsChart from './MonthlyVehicleContractsChart.vue';
import MonthlyRealisationPriceChart from './MonthlyRealisationPriceChart.vue';

import { mapCostReportRows, setVehicleTypeRows } from '../../../service/report_service';
import { VehicleCostMonthlyRow } from '../../../../../common/application/model/VehicleCostMonthlyRow';
import { Customer } from '../../../../../common/application/model/Customer';
import { getAccessibleCustomers } from '../../../service/resource_service';
import { CustomerReportVehicleTypeRow } from '../../../../../common/application/model/report/CustomerReportVehicleTypeRow';
import { CustomerReport } from '../../../../../common/application/model/report/CustomerReport';
import { CustomerMakesReport } from '../../../../../common/application/model/report/CustomerMakesReport';
import { getMultipleCustomerFleetMakeReport, getMultipleCustomerFleetReport } from '../../../client/report_client';
import { CostReport } from '../../../../../common/application/model/report/CostReport';
import { getDatabaseOptions, OptionValue } from '../../../../framework/service/options';
import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';
import ParameterService from '../../../service/parameter_service';
import { VehicleUsage } from '../../../../../common/application/model/VehicleUsage';
import { getVehicleUsageForCustomer } from '../../../client/vehicle_usage_client';

@Component({
    components: {
        BudgetSpent,
        VehicleTypeTable,
        VehicleStateChart,
        BudgetTable,
        CostTypeChart,
        MonthlyBudgetChart,
        VehicleMakeTable,
        VehicleMakeChart,
        AutoCompleteMultipleField,
        VehicleMonthlyMileageChart,
        VehicleMonthlyConsumptionChart,
        VehicleMonthlyCO2Chart,
        VehicleMonthlyAverageMileageChart,
        VehicleStates,
        MonthlyVehicleContractsChart,
        MonthlyRealisationPriceChart,
    },
})
export default class Reports extends Vue {
    readonly resourceType = ApplicationResource.REPORT;
    parameterService = new ParameterService(this.resourceType);

    @Prop(String) readonly customerId!: string;

    shared = sharedState;

    local = {
        costReport: [] as CostReport[],
        customers: [] as Customer[],
        customerFleetReport: {} as CustomerReport,
        invoiceRowTypeOptions: [] as OptionValue[],
        loading: false,
        rows: [] as VehicleCostMonthlyRow[],
        showWithVat: false,
        selectedMonth: undefined as string | undefined,
        totalBalance: 0,
        vehicleMakesRows: [] as CustomerMakesReport[],
        vehicleTypeRows: [] as CustomerReportVehicleTypeRow[],
        vehicleUsageData: [] as VehicleUsage[],
        parameters: this.parameterService.get(),
        budgetYear: new Date().getFullYear(),
        previousBudgetYear: new Date().getFullYear() - 1,
        showBudget: true,
        showOutcome: true,
        showPreviousBudget: true,
        showPreviousOutcome: true,
        prevYearCustomerFleetReport: {} as CustomerReport,
    };

    async mounted() {
        if (this.$route.params && this.$route.params.customerId) {
            this.local.parameters.customerId = this.$route.params.customerId.split(',');
            this.parameterService.set(this.local.parameters);
        }

        if (!this.local.parameters.customerId || !this.local.parameters.customerId.length) {
            this.local.parameters.customerId = this.local.customers.map(({ id }) => id);
        }

        if (new Date().getDate() >= 15) {
            this.local.selectedMonth = dateToDbMonthDateString(DateTime.now().minus({ months: 1 }).toJSDate());
        } else {
            this.local.selectedMonth = dateToDbMonthDateString(DateTime.now().minus({ months: 2 }).toJSDate());
        }

        await this.loadAsyncData();
    }

    async dropdownClosed(resourceIds: string[], idField: string): Promise<void> {
        if (this.local.loading) {
            return;
        }

        if (!resourceIds || !resourceIds.length) {
            delete this.local.parameters[idField];
        } else {
            this.local.parameters[idField] = resourceIds;
        }

        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    @Watch('local.selectedMonth')
    async selectedMonthChange() {
        if (this.local.loading) {
            return;
        }

        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    @Watch('local.budgetYear')
    async budgetYearChange() {
        if (this.local.loading) {
            return;
        }

        this.local.previousBudgetYear = this.local.budgetYear - 1;

        await this.loadCostReport();
    }

    dateToMonthDateString(date: Date) {
        return dateToDbMonthDateString(date);
    }

    dateToMonthDateSlashString(date: Date) {
        return DateTime.fromJSDate(date).toFormat('MM/yyyy');
    }

    nextMonth(): void {
        this.selectedMonth = DateTime.fromJSDate(this.selectedMonth).plus({ months: 1 }).toJSDate();
    }

    previousMonth(): void {
        this.selectedMonth = DateTime.fromJSDate(this.selectedMonth).minus({ months: 1 }).toJSDate();
    }

    get selectedMonth(): Date {
        if (!this.local.selectedMonth) {
            return new Date();
        }
        return DateTime.fromFormat(this.local.selectedMonth, 'yyyy-MM').toJSDate();
    }

    set selectedMonth(selectedMonth) {
        this.local.selectedMonth = dateToDbMonthDateString(DateTime.fromJSDate(selectedMonth).toJSDate());
    }

    get monthsIncluded(): string {
        if (!this.local.selectedMonth) {
            return '-';
        }

        return `${DateTime.now().startOf('year').toFormat('MM/yyyy')} - ${DateTime.fromFormat(
            this.local.selectedMonth,
            'yyyy-MM',
        ).toFormat('MM/yyyy')}`;
    }

    private async loadAsyncData() {
        this.local.loading = true;

        this.local.invoiceRowTypeOptions = await getDatabaseOptions('InvoiceRowType', this);
        this.local.customers = await getAccessibleCustomers();

        if (this.local.parameters.customerId.length) {
            this.local.vehicleMakesRows = await getMultipleCustomerFleetMakeReport(this.local.parameters.customerId);
            this.local.vehicleUsageData = await getVehicleUsageForCustomer(this.local.parameters.customerId);
        }

        await this.loadCostReport();

        this.local.loading = false;
    }

    private async loadCostReport(): Promise<void> {
        this.local.loading = true;
        if (this.local.parameters.customerId && this.local.parameters.customerId.length) {
            this.local.customerFleetReport = await getMultipleCustomerFleetReport(
                this.local.parameters.customerId,
                this.local.budgetYear,
            );

            this.local.prevYearCustomerFleetReport = await getMultipleCustomerFleetReport(
                this.local.parameters.customerId,
                this.local.previousBudgetYear,
            );
        }

        this.local.vehicleTypeRows = setVehicleTypeRows(this.local.customerFleetReport, this);

        let currentMonth = this.dateToMonthDateString(new Date());

        if (this.local.selectedMonth) {
            currentMonth = this.local.selectedMonth;
        }

        this.setCostReport(currentMonth);
        this.local.loading = false;
    }

    private setCostReport(monthAsString: string) {
        if (!this.local.customerFleetReport || !Object.keys(this.local.customerFleetReport).length) {
            return;
        }

        this.local.costReport = mapCostReportRows(
            this.local.customerFleetReport.costRows,
            [monthAsString],
            this.local.invoiceRowTypeOptions,
        );
    }
}
